/**
 * Pre-workshop questionnaire modal. Two layers, deliberately separate:
 *
 *   - DRAFTS — every keystroke persists per-workshop in `useQuestionnaires`
 *     (IndexedDB, this browser only), so closing the modal or reloading
 *     never loses answers (UAT round 1).
 *   - SUBMISSION — the footer's SUBMIT RESPONSE snapshots the drafts into
 *     `useQuestionnaireResponses`: one response per user per workshop,
 *     written through to the BFF `questionnaires` tier in prod so the
 *     facilitation side can aggregate them in the Asset Locker results view.
 *
 * Ported from:
 *   - `_legacy/wd.html` lines 420-441 — `#questionnaireModal` shell +
 *     "SUBMIT RESPONSE" footer button.
 *   - `_legacy/xaes-content.js` lines 607-700 — `renderQuestionnaireModal()`
 *     question loop.
 *   - `_legacy/xaes-content.js` lines 251-279 — WS4 questionnaire
 *     intro + questions. The vanilla version mixed radio / scale /
 *     checkbox / textarea types; Phase 2 collapsed those to text-only and
 *     the results work restores `choice` + `scale` so the facilitator
 *     view has chartable answers.
 *
 * Chrome contract:
 *   - Uses `PanelDialogContent` + the legacy `.modal-shell` markup so
 *     this briefing modal matches the Wave-4 Asset Locker output modals.
 */

import { useEffect, useState } from "react";
import { ClipboardList, X } from "lucide-react";
import { WorkshopLabelPill } from "@/components/tags/WorkshopLabelPill";
import { Dialog, DialogDescription, DialogTitle } from "@/components/ui/dialog";
import { PanelDialogContent } from "@/components/shell/PanelDialogContent";
import { cn } from "@/lib/utils";
import { useAuth } from "@/hooks/use-auth";
import { useQuestionnaires } from "@/stores/useQuestionnaires";
import { useQuestionnaireResponses, buildQuestionnaireSearchableText } from "@/stores/useQuestionnaireResponses";
import type { Workshop } from "@/content/workshops";
import type { QuestionnaireContent, QuestionnaireQuestion } from "./modal-content";

type QuestionnaireModalProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  workshopId: string;
  questionnaire: QuestionnaireContent;
};

// Stable empty selector result so an unanswered workshop doesn't produce a
// fresh object (and a spurious re-render) on every store change.
const EMPTY_ANSWERS: Record<string, string> = {};

const FIELD_CLASS = cn(
  "border-border/60 bg-background/60 text-foreground placeholder:text-muted-foreground/60",
  "focus-visible:ring-ring w-full rounded-md border px-3 py-2 text-sm",
  "focus-visible:ring-2 focus-visible:outline-none transition"
);

export function QuestionnaireModal({
  open,
  onOpenChange,
  workshopId,
  questionnaire,
}: QuestionnaireModalProps) {
  const hydrate = useQuestionnaires((s) => s.hydrate);
  const storeSetAnswer = useQuestionnaires((s) => s.setAnswer);
  const answers = useQuestionnaires(
    (s) => s.answersByWorkshop[workshopId] ?? EMPTY_ANSWERS
  );

  const auth = useAuth();
  const username = auth.user?.username ?? "anon";
  const wsId = workshopId as Workshop["id"];
  const hydrateResponses = useQuestionnaireResponses((s) => s.hydrate);
  const submitResponse = useQuestionnaireResponses((s) => s.submit);
  const existingSubmission = useQuestionnaireResponses(
    (s) =>
      s.entriesByWorkshop[wsId]?.find((r) => r.username === username) ?? null,
  );

  const [submitState, setSubmitState] = useState<
    "idle" | "submitting" | "submitted" | "error"
  >("idle");
  const [submitError, setSubmitError] = useState<string | null>(null);

  useEffect(() => {
    void hydrate();
    void hydrateResponses();
  }, [hydrate, hydrateResponses]);

  const completedCount = questionnaire.questions.filter(
    (q) => (answers[q.id] ?? "").trim().length > 0
  ).length;
  const allAnswered = completedCount === questionnaire.questions.length;

  function setAnswer(id: string, value: string): void {
    storeSetAnswer(workshopId, id, value);
    // Editing after a submit re-arms the button (drafts have diverged).
    setSubmitState((s) => (s === "submitted" ? "idle" : s));
  }

  async function handleSubmit(): Promise<void> {
    if (submitState === "submitting") return;
    setSubmitState("submitting");
    setSubmitError(null);
    try {
      const answered = snapshotAnswers(questionnaire.questions, answers);
      await submitResponse(wsId, {
        workshopId: wsId,
        username,
        displayName: auth.user?.displayName ?? username,
        role: auth.user?.role ?? "",
        submittedAt: Date.now(),
        answers: answered,
        searchableText: buildQuestionnaireSearchableText(
          questionnaire.questions,
          answered,
        ),
      });
      setSubmitState("submitted");
    } catch (err) {
      // The drafts (and the local copy of the response) are already saved —
      // only the durable mirror write failed. Say so, don't fail silently.
      setSubmitState("error");
      setSubmitError(
        err instanceof Error ? err.message : "Submission failed — try again.",
      );
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <PanelDialogContent
        className="w-[90vw] max-w-[820px] border-0 bg-transparent p-0 shadow-none"
        style={{ outline: "none" }}
      >
        <DialogTitle className="sr-only">
          Pre-Workshop Questionnaire — {workshopId}
        </DialogTitle>
        <DialogDescription className="sr-only">
          {questionnaire.intro}
        </DialogDescription>
        <div
          className="modal-shell"
          style={{ width: "100%", maxHeight: "85vh" }}
        >
          {/* HEADER */}
          <div className="modal-header">
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <ClipboardList
                aria-hidden
                style={{ width: 16, height: 16, color: "var(--accent-true-blue)" }}
              />
              <span className="modal-title">QUESTIONNAIRE</span>
              <WorkshopLabelPill workshopId={workshopId} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  fontSize: "var(--fs-micro)",
                  color: "var(--text-muted)",
                  letterSpacing: 2,
                  textTransform: "uppercase",
                  marginRight: 12,
                }}
              >
                {completedCount} / {questionnaire.questions.length} answered
              </span>
              <button
                type="button"
                onClick={() => onOpenChange(false)}
                className="modal-close-btn"
                aria-label="Close"
              >
                <X aria-hidden style={{ width: 12, height: 12 }} />
                CLOSE
              </button>
            </div>
          </div>

          {/* BODY */}
          <div className="modal-body">
            <div className="dt-pane dt-pane-active">
              <p className="text-muted-foreground pb-3 text-xs leading-relaxed">
                {questionnaire.intro}
              </p>
              <div className="flex flex-col gap-4">
                {questionnaire.questions.map((q, index) => {
                  const value = answers[q.id] ?? "";
                  return (
                    <div key={q.id} className="flex flex-col gap-1.5">
                      <label
                        htmlFor={`q-${q.id}`}
                        className="text-foreground text-sm font-semibold"
                      >
                        <span className="text-muted-foreground mr-1.5">
                          {index + 1}.
                        </span>
                        {q.label}
                      </label>
                      <QuestionField
                        question={q}
                        value={value}
                        onChange={(v) => setAnswer(q.id, v)}
                      />
                    </div>
                  );
                })}
              </div>
              {submitState === "error" && submitError ? (
                <p
                  role="alert"
                  className="pt-4 text-xs"
                  style={{ color: "var(--accent-danger)" }}
                >
                  {submitError} Your answers are saved on this device.
                </p>
              ) : (
                <p className="text-muted-foreground pt-4 text-fs-label tracking-wider uppercase">
                  {existingSubmission
                    ? `Submitted ${new Date(existingSubmission.submittedAt).toLocaleString()} — edit and resubmit any time.`
                    : "Answers save on this device as you type — submit to share them with the facilitation team."}
                </p>
              )}
            </div>
          </div>

          {/* FOOTER — the legacy "SUBMIT RESPONSE" slot, now live. */}
          <div className="modal-footer">
            <span className="modal-footer-label">XAES Phase Zero</span>
            <span className="modal-footer-sep">|</span>
            <span className="modal-footer-ws">
              Workshop Brief — Questionnaire
            </span>
            <span
              style={{
                marginLeft: "auto",
                display: "flex",
                alignItems: "center",
                gap: 10,
              }}
            >
              {submitState === "submitted" && (
                <span
                  style={{
                    fontSize: "var(--fs-micro)",
                    color: "var(--accent-green)",
                    letterSpacing: 1.5,
                    textTransform: "uppercase",
                  }}
                >
                  Response submitted
                </span>
              )}
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={
                  submitState === "submitting" ||
                  submitState === "submitted" ||
                  !allAnswered
                }
                className="modal-close-btn"
                title={
                  allAnswered
                    ? "Submit your responses to the facilitation team"
                    : "Answer every question to submit"
                }
                style={{
                  color: allAnswered
                    ? "var(--accent-true-blue)"
                    : "var(--text-muted)",
                  opacity:
                    submitState === "submitting" || submitState === "submitted"
                      ? 0.6
                      : 1,
                }}
              >
                {submitState === "submitting"
                  ? "SUBMITTING…"
                  : submitState === "submitted"
                    ? "SUBMITTED"
                    : "SUBMIT RESPONSE"}
              </button>
            </span>
          </div>
        </div>
      </PanelDialogContent>
    </Dialog>
  );
}

/**
 * Snapshot the string drafts into a submission's typed answers: scale
 * questions submit as numbers (the results view charts them); everything
 * else stays a string. Blank drafts are omitted.
 */
function snapshotAnswers(
  questions: QuestionnaireQuestion[],
  drafts: Record<string, string>,
): Record<string, string | number> {
  const out: Record<string, string | number> = {};
  for (const q of questions) {
    const raw = (drafts[q.id] ?? "").trim();
    if (!raw) continue;
    if (q.type === "scale") {
      const n = Number(raw);
      out[q.id] = Number.isFinite(n) ? n : raw;
    } else {
      out[q.id] = raw;
    }
  }
  return out;
}

/** Inclusive scale steps, defaulting to the legacy 1–5. */
function scaleSteps(q: QuestionnaireQuestion): number[] {
  const min = q.min ?? 1;
  const max = q.max ?? 5;
  if (max <= min) return [min];
  return Array.from({ length: max - min + 1 }, (_, i) => min + i);
}

/**
 * One answer field, dispatched on the question type. Choice + scale render
 * as selectable pills but still write plain strings into the draft store —
 * the store (and the Brief row's answered-count) stays string-only; the
 * scale→number conversion happens at submit time in `snapshotAnswers`.
 */
function QuestionField({
  question,
  value,
  onChange,
}: {
  question: QuestionnaireQuestion;
  value: string;
  onChange: (value: string) => void;
}) {
  if (question.type === "textarea") {
    return (
      <textarea
        id={`q-${question.id}`}
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={question.placeholder}
        rows={3}
        className={cn(FIELD_CLASS, "resize-y")}
      />
    );
  }
  if (question.type === "choice") {
    return (
      <div
        id={`q-${question.id}`}
        role="radiogroup"
        aria-label={question.label}
        className="flex flex-wrap gap-1.5"
      >
        {(question.options ?? []).map((option) => (
          <OptionPill
            key={option}
            label={option}
            selected={value === option}
            onSelect={() => onChange(option)}
          />
        ))}
      </div>
    );
  }
  if (question.type === "scale") {
    return (
      <div
        id={`q-${question.id}`}
        role="radiogroup"
        aria-label={question.label}
        className="flex items-center gap-1.5"
      >
        {scaleSteps(question).map((step) => (
          <OptionPill
            key={step}
            label={String(step)}
            selected={value === String(step)}
            onSelect={() => onChange(String(step))}
            narrow
          />
        ))}
        {question.placeholder && (
          <span
            className="text-muted-foreground pl-2"
            style={{ fontSize: "var(--fs-label)" }}
          >
            {question.placeholder}
          </span>
        )}
      </div>
    );
  }
  return (
    <input
      id={`q-${question.id}`}
      type="text"
      value={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={question.placeholder}
      className={FIELD_CLASS}
    />
  );
}

/** Selectable pill shared by choice options and scale steps. */
function OptionPill({
  label,
  selected,
  onSelect,
  narrow = false,
}: {
  label: string;
  selected: boolean;
  onSelect: () => void;
  narrow?: boolean;
}) {
  return (
    <button
      type="button"
      role="radio"
      aria-checked={selected}
      onClick={onSelect}
      className="rounded-md border text-sm transition"
      style={{
        padding: narrow ? "6px 0" : "6px 12px",
        width: narrow ? 34 : undefined,
        textAlign: "center",
        cursor: "pointer",
        borderColor: selected
          ? "var(--accent-true-blue)"
          : "hsl(var(--border) / 0.6)",
        background: selected
          ? "rgba(var(--accent-true-blue-rgb), 0.12)"
          : "var(--bg-card)",
        color: selected ? "var(--accent-true-blue)" : "var(--text-secondary)",
        fontWeight: selected ? 600 : 400,
      }}
    >
      {label}
    </button>
  );
}
